import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, MobileHeader, Segmented, EmptyState, useToast } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useCertificates } from '@/hooks/queries';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useLocale } from '@/i18n/LocaleProvider';
import type { Locale } from '@/types/domain';
import { exportNodeAsPng } from '@/lib/certExport';
import { CertFormal, CertMinimal, certPayload } from '../certificate/Certificate';

export function LearnerCertView() {
  const { certId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const { locale } = useLocale();
  const { data: certs = [] } = useCertificates();
  const { toast } = useToast();
  const [treatment, setTreatment] = useState<'formal' | 'minimal'>('formal');
  // The holder name renders in the chosen language (correct prefix/suffix per
  // language); defaults to the learner's current app language.
  const [certLang, setCertLang] = useState<Locale>(locale);
  // Off-screen, full-size cert is the source we rasterise to PNG for download.
  const captureRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);

  const cert = certs.find((c) => c.id === certId);
  if (!cert || !user) {
    return <EmptyState icon="award" title="Certificate not found" action={<button className="btn btn-ghost btn-sm" onClick={() => navigate('/learn/wallet')}>Back to wallet</button>} />;
  }

  const d = certPayload(cert, user, certLang);
  const certW = isDesktop ? 680 : 338;
  const Cert = treatment === 'formal' ? CertFormal : CertMinimal;

  // Real-file download — works inside LINE/mobile WebViews where window.print()
  // does nothing. Captures the off-screen full-size cert → PNG → share/download.
  async function handleDownload() {
    const node = captureRef.current;
    if (!node || saving || !cert) return;
    setSaving(true);
    try {
      await exportNodeAsPng(node, `${cert.id}.png`);
      toast('Certificate saved', { tone: 'sage', icon: 'download' });
    } catch {
      toast('Could not generate image — try Print / Save as PDF', { tone: 'clay', icon: 'x' });
    } finally {
      setSaving(false);
    }
  }

  const meta: [string, string, boolean?][] = [
    ['Certificate ID', cert.id, true],
    ['Host', cert.host],
    ['Instructor', cert.instructor],
    ['Issued', cert.issued],
    ['Expires', cert.expires],
  ];

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader
        title="Certificate"
        th="ใบรับรอง"
        onBack={() => navigate(-1)}
        large={false}
        right={<button onClick={handleDownload} disabled={saving} style={{ padding: 9, borderRadius: 99, border: 'none', background: 'transparent', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1 }}><Icon name="download" size={20} /></button>}
      />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: isDesktop ? 32 : '16px 20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {isDesktop && (
          <button onClick={() => navigate('/learn/wallet')} className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}>
            <Icon name="chevL" size={15} /> Back to wallet
          </button>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Segmented value={treatment} onChange={setTreatment} options={[{ value: 'formal', label: 'Formal' }, { value: 'minimal', label: 'Minimal' }]} />
          <Segmented value={certLang} onChange={setCertLang} options={[{ value: 'en', label: 'EN' }, { value: 'th', label: 'ไทย' }]} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Cert d={d} w={certW} />
        </div>

        <div className="card divide">
          {meta.map(([l, v, mono], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 16px' }}>
              <span style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>{l}</span>
              <span style={{ fontSize: 14, fontWeight: 500 }} className={mono ? 'u-mono' : ''}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 14px', background: 'var(--sage-tint)', borderRadius: 10, fontSize: 13, color: 'var(--sage-deep)' }}>
          <Icon name="qr" size={16} style={{ flex: 'none', marginTop: 1 }} />
          <span>Anyone can verify this certificate by scanning the QR code — it confirms the ID, holder and expiry against hospital records.</span>
        </div>

        <button className="btn btn-primary btn-lg btn-block" onClick={handleDownload} disabled={saving}>
          <Icon name="download" size={18} /> {saving ? 'Preparing…' : 'Download certificate'}
        </button>
        <button className="btn btn-ghost btn-block" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
        <p style={{ margin: '-6px 0 0', fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center', lineHeight: 1.5 }}>
          <strong>Download</strong> saves an image to your phone — works in LINE/Telegram. ·
          บันทึกรูปภาพลงเครื่อง ใช้ได้ในไลน์
        </p>
      </div>

      {/* Off-screen full-size cert: the rasterisation source for Download.
          Rendered (not display:none) so html-to-image has real layout, parked
          far off-screen so it never shows or affects scroll. */}
      <div aria-hidden style={{ position: 'fixed', left: -10000, top: 0, pointerEvents: 'none' }}>
        {/* The ref is on this INNER wrapper, NOT the off-screen one: html-to-image
            copies the captured node's own inline styles into its render sandbox,
            so if the target carried `left:-10000` the clone would paint off-canvas
            → blank PNG. The offset must stay on the outer element only. */}
        <div ref={captureRef}>
          <Cert d={d} w={1040} />
        </div>
      </div>

      {/* Print: render a full-size A4 cert into <body> (a sibling of #root) and
          display:none the whole app in print — one clean page, no trailing blank
          page (the old visibility:hidden kept the app's layout height). */}
      {createPortal(
        <>
          <div className="cert-print-only" aria-hidden>
            <Cert d={d} w={1040} />
          </div>
          {/* Print styles live in the portal (a direct <body> child) so the @page
              landscape rule isn't trapped inside the display:none #root subtree. */}
          <style>{`
            .cert-print-only { display: none; }
            @media print {
              #root { display: none !important; }
              .cert-print-only { display: flex !important; justify-content: center; align-items: flex-start; }
              .cert-print-only, .cert-print-only * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              html, body { background: #fff !important; }
              @page { size: A4 landscape; margin: 6mm; }
            }
          `}</style>
        </>,
        document.body,
      )}
    </div>
  );
}
